import type { CSSProperties, ReactEventHandler, Ref } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { cn } from '@opendoor/atoms/utils';
import { withImageParams } from 'helpers/imageUrl';
import Head from 'next/head';

export type PictureSourceSpec = Array<{
  srcSet: string;
  media: string;
  key: string;
  fallbackSrc?: string;
  sizes?: string;
}>;

// Last-resort fallback when no <source> matches and no real fallback URL exists.
const TRANSPARENT_PIXEL_DATA_URI =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const OBJECT_FIT_CLASS: Record<string, string> = {
  cover: 'object-cover',
  contain: 'object-contain',
  fill: 'object-fill',
  none: 'object-none',
  'scale-down': 'object-scale-down',
};

type PictureProps = {
  preload: boolean;
  lazy: boolean;
  rounded?: string;
  sources: PictureSourceSpec;
  fallbackSrc?: string;
  objectFit?: CSSProperties['objectFit'];
  imageRef?: Ref<HTMLImageElement>;
  fetchPriority?: 'high' | 'low' | 'auto';
  onLoad?: ReactEventHandler<HTMLImageElement>;
  onError?: ReactEventHandler<HTMLImageElement>;
  alt?: string;
};

export const Picture = ({
  preload,
  lazy,
  rounded,
  sources,
  fallbackSrc,
  objectFit = 'cover',
  imageRef,
  fetchPriority,
  onLoad,
  onError,
  alt = '',
}: PictureProps) => {
  const imgSrc = fallbackSrc ?? TRANSPARENT_PIXEL_DATA_URI;
  const objectFitClass = OBJECT_FIT_CLASS[objectFit ?? 'cover'] ?? 'object-cover';
  const preloadFetchPriorityProps = { fetchpriority: fetchPriority ?? 'high' };

  const pictureElement = (
    <Image
      ref={imageRef}
      src={imgSrc}
      alt={alt}
      sources={sources}
      className={cn('block h-full w-full', objectFitClass)}
      pictureClassName="block h-full w-full"
      loading={lazy ? 'lazy' : 'eager'}
      fetchPriority={fetchPriority}
      onLoad={onLoad}
      onError={onError}
    />
  );

  return (
    <>
      {preload && (
        <Head>
          {sources.map(({ srcSet, media, key, fallbackSrc: sourceFallbackSrc, sizes }) => {
            return (
              <link
                rel="preload"
                as="image"
                href={sourceFallbackSrc ?? imgSrc}
                imageSrcSet={srcSet}
                imageSizes={sizes}
                media={media}
                key={`${key}-preload`}
                {...preloadFetchPriorityProps}
              />
            );
          })}
        </Head>
      )}
      {rounded ? (
        <Box className={cn('block h-full w-full overflow-hidden', rounded)}>{pictureElement}</Box>
      ) : (
        pictureElement
      )}
    </>
  );
};

export type PresetPictureSet = Array<{
  minWidth: number;
  maxWidth: number | null;
  // List of preset values here: https://github.com/opendoor-labs/open_listings/blob/dd5e7230ae24d2e79181cb89485efa79aadee705/app/helpers/image_cdn.rb#L6
  preset: string;
  presetScale?: string;
}>;
export type PresetPictureSetOptions = {
  photoUrl: string;
  sizes: PresetPictureSet;
  rounded?: string;
  lazy: boolean;
  dpr?: Array<number> | null;
  preload?: boolean;
  objectFit?: CSSProperties['objectFit'];
  imageRef?: Ref<HTMLImageElement>;
  fit?: 'cover' | 'contain';
  fetchPriority?: 'high' | 'low' | 'auto';
  onLoad?: ReactEventHandler<HTMLImageElement>;
  onError?: ReactEventHandler<HTMLImageElement>;
  quality?: number;
  alt?: string;
};

export const generatePresetPictureSetProps = ({
  photoUrl,
  sizes,
  rounded,
  lazy,
  dpr = [1, 2, 3],
  preload = false,
  objectFit,
  fit,
  fetchPriority,
  onLoad,
  onError,
  quality,
  imageRef,
  alt,
}: PresetPictureSetOptions): PictureProps => {
  // oxlint-disable-next-line unicorn/no-array-sort -- Cosmos still targets browsers without Array.prototype.toSorted; this sorts a copy.
  const sortedSizes = [...sizes].sort((a, b) => a.minWidth - b.minWidth);
  const dprValues = dpr?.length ? dpr : null;
  const sources: PictureSourceSpec = sortedSizes.map(
    ({ minWidth, maxWidth, preset, presetScale }) => {
      const paramsRecord: Record<string, string> = {
        preset,
        ...(presetScale ? { presetScale } : {}),
        service: 'cosmos',
        auto: 'format,compress',
        ...(fit ? { fit } : {}),
        ...(quality !== undefined ? { q: `${quality}` } : {}),
      };
      const sourceFallbackSrc = withImageParams(photoUrl, {
        ...paramsRecord,
        ...(dprValues ? { dpr: '1' } : {}),
      });
      const srcSet = dprValues
        ? dprValues
            .map((d) => {
              const finalParams: Record<string, string> = { ...paramsRecord, dpr: `${d}` };
              return `${withImageParams(photoUrl, finalParams)} ${d}x`;
            })
            .join(', ')
        : sourceFallbackSrc;
      const media =
        maxWidth === null
          ? `(min-width: ${minWidth}px)`
          : `(min-width: ${minWidth}px) and (max-width: ${maxWidth - 1}px)`;
      return { srcSet, media, key: `${photoUrl}-${minWidth}`, fallbackSrc: sourceFallbackSrc };
    },
  );
  const smallest = sortedSizes[0];
  const fallbackSrc = smallest
    ? withImageParams(photoUrl, {
        preset: smallest.preset,
        ...(smallest.presetScale ? { presetScale: smallest.presetScale } : {}),
        service: 'cosmos',
        auto: 'format,compress',
        ...(fit ? { fit } : {}),
        ...(quality !== undefined ? { q: `${quality}` } : {}),
        ...(dprValues ? { dpr: '1' } : {}),
      })
    : undefined;
  return {
    preload,
    lazy,
    rounded,
    sources,
    fallbackSrc,
    objectFit,
    imageRef,
    fetchPriority,
    onLoad,
    onError,
    alt,
  };
};
